import { types } from "@/lib/openapi";

export type ArtefactGroupWithArtefacts = types.ArtefactGroupWithArtefacts;
export interface ArtefactMetaGroupWithArtefacts
    extends ArtefactGroupWithArtefacts {
    identity: string;
    name: string;
    artefacts: ArtefactMeta[] | ArtefactMetaGroupWithArtefacts[];
}

export type Hateoas = types.Hateoas;

export interface ArtefactMeta<MimeType extends string = string>
    extends types.Artefact {
    identity: string;
    name: string;
    mimetype: MimeType;
    revision?: string;
    links?: Record<string, Hateoas>;
}

export interface BpmnArtefact extends Artefact<"application/bpmn+xml"> {
    preview?: string;
}

export type MarkdownArtefact = Artefact<"text/markdown">;

export type Contributor = types.Contributor;
export type HistoryEntry = types.HistoryEntry;

export class Artefact<MimeType extends string = string>
    implements ArtefactMeta
{
    // Internal string text representation of the blob.
    // This is kept separate from blob since it can only be
    // read async from the blob.
    // Get/set content is used for this, because also the
    // blob needs to be updated.
    private _content: string;
    private _originalContent: string;
    // Internal store of the blob, is kept seperately from
    // _content because the blob can be purely binary as well.
    private _blob: Blob;
    private _originalBlob: Blob;
    private _name: string;
    private _originalName: string;
    identity: string;
    mimetype: MimeType;
    revision?: string;
    links?: Record<string, Hateoas>;

    constructor(object: {
        // Content is required here since it can only be read async from
        // the blob. This would cause an unexpected race-condition for the
        // content value.
        content: string;
        blob: Blob;
        identity?: string;
        name: string;
        mimetype: MimeType;
        revision?: string;
        links?: Record<string, Hateoas>;
    }) {
        this._content = object.content;
        this._originalContent = this._content;
        this._blob = object.blob;
        this._originalBlob = this._blob;
        this.identity = object.identity ?? "";
        this._name = object.name;
        this._originalName = this._name;
        this.mimetype = object.mimetype;
        this.revision = object.revision;
        this.links = object.links;
    }

    get name() {
        return this._name;
    }

    set name(name: string) {
        this._name = name;
    }

    get blob() {
        return this._blob;
    }

    set blob(blob: Blob) {
        // Sadly this is async, but it will update after reading.
        blob.text().then((content) => (this._content = content));
        this._blob = blob;
        this.mimetype = (blob.type as MimeType) || this.mimetype;
    }

    get content() {
        return this._content;
    }

    set content(content: string) {
        this._content = content;
        this._blob = new Blob([content], { type: this.mimetype });
    }

    get isModified() {
        return this._content !== this._originalContent;
    }

    get isNameModified() {
        return this._name !== this._originalName;
    }

    /**
     * Get the data URL based on the current blob.
     * Useful for rendering binary files.
     */
    getDataUrl() {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = ({ target }) => {
                resolve(target!.result as string);
            };
            reader.onerror = (error) => {
                reject(error);
            };
            reader.onabort = (error) => {
                reject(error);
            };
            reader.readAsDataURL(this.blob);
        });
    }

    /**
     * Create an Artefact without any contents.
     */
    static createEmptyArtefact<MimeType extends string = string>(object: {
        name?: string;
        identity?: string;
        mimetype: MimeType;
    }) {
        return new Artefact<MimeType>({
            name: object.name ?? "",
            identity: object.identity ?? "",
            mimetype: object.mimetype,
            content: "",
            blob: new Blob([], { type: object.mimetype }),
        });
    }

    /**
     * Create an Artefact with BPMN mimetype.
     */
    static async createBpmnArtefact(object: {
        name: string;
        blob: Blob;
        identity?: string;
    }): Promise<BpmnArtefact> {
        return new Artefact({
            ...object,
            mimetype: "application/bpmn+xml",
            content: await object.blob.text(),
        });
    }

    /**
     * Write new content and update the blob.
     */
    async updateContent(content: string | Blob) {
        if (typeof content === "string") {
            this._content = content;
            this._blob = new Blob([content], { type: this.mimetype });
        } else {
            this._blob = content;
            this._content = await content.text();
            this.mimetype = (content.type as MimeType) || this.mimetype;
        }
    }

    /**
     * Revert both the content and the blob based on the originalBlob and
     * disable isModified.
     */
    async resetContent() {
        this.content = await this._originalBlob.text();
        this.blob = this._originalBlob;
    }

    /**
     * Disable isModified and set the originalBlob to the modified content.
     */
    async persist() {
        this._originalBlob = this.blob;
        this._originalContent = this._content;
    }
}
